import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Alert,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    Add as AddIcon,
    CardGiftcard as GiftCardIcon,
    ContentCopy as CopyIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import { giftCardAPI } from '../../services/api';
import { formatDateForInput, parseDateOnlyString } from '../../utils/dateUtils';

type StatusFilter = 'active' | 'redeemed' | 'all';

interface GiftCard {
    id: string;
    code: string;
    denomination: number;
    notes: string | null;
    expiresAt: string | null;
    redeemedAt: string | null;
    redeemedByMemberId: string | null;
    redeemedByMember: { id: string; firstName: string; lastName: string } | null;
    createdAt: string;
}

const emptyForm = {
    denomination: '',
    notes: '',
    expiresAt: '',
};

const GiftCardsPage: React.FC = () => {
    const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [creating, setCreating] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const [copiedCode, setCopiedCode] = useState<string | null>(null);

    // Email gift card state
    const [emailDialogCard, setEmailDialogCard] = useState<GiftCard | null>(null);
    const [emailRecipientEmail, setEmailRecipientEmail] = useState('');
    const [emailRecipientName, setEmailRecipientName] = useState('');
    const [emailSending, setEmailSending] = useState(false);
    const [emailFormError, setEmailFormError] = useState<string | null>(null);

    const fetchGiftCards = async (filter: StatusFilter = statusFilter) => {
        setLoading(true);
        setError(null);
        try {
            const response = await giftCardAPI.getAll(1, 200, filter);
            setGiftCards(response.data.items);
            setTotal(response.data.total);
        } catch (err: any) {
            setError(err?.response?.data?.error || 'Failed to fetch gift cards.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGiftCards(statusFilter);
    }, [statusFilter]);

    const handleOpenCreate = () => {
        setForm(emptyForm);
        setFormError(null);
        setCreateDialogOpen(true);
    };

    const handleCreateSubmit = async () => {
        if (!form.denomination || parseFloat(form.denomination) <= 0) {
            setFormError('Please enter a valid denomination.');
            return;
        }
        setCreating(true);
        setFormError(null);
        try {
            await giftCardAPI.create({
                denomination: parseFloat(form.denomination),
                notes: form.notes || undefined,
                expiresAt: form.expiresAt || undefined,
            });
            setCreateDialogOpen(false);
            setSuccess('Gift card created successfully!');
            setTimeout(() => setSuccess(null), 5000);
            await fetchGiftCards(statusFilter);
        } catch (err: any) {
            setFormError(err?.response?.data?.error || 'Failed to create gift card.');
        } finally {
            setCreating(false);
        }
    };

    const handleCopyCode = (code: string) => {
        navigator.clipboard.writeText(code).then(() => {
            setCopiedCode(code);
            setTimeout(() => setCopiedCode(null), 2000);
        });
    };

    const handleOpenEmailDialog = (card: GiftCard) => {
        setEmailDialogCard(card);
        setEmailRecipientEmail('');
        setEmailRecipientName('');
        setEmailFormError(null);
    };

    const handleSendGiftCardEmail = async () => {
        if (!emailDialogCard) return;
        if (!emailRecipientEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailRecipientEmail)) {
            setEmailFormError('Please enter a valid email address.');
            return;
        }
        setEmailSending(true);
        setEmailFormError(null);
        try {
            await giftCardAPI.emailGiftCard(emailDialogCard.id, {
                recipientEmail: emailRecipientEmail,
                recipientName: emailRecipientName || undefined,
            });
            setEmailDialogCard(null);
            setSuccess(`Gift card emailed to ${emailRecipientEmail}`);
            setTimeout(() => setSuccess(null), 5000);
        } catch (err: any) {
            setEmailFormError(err?.response?.data?.error || 'Failed to send email.');
        } finally {
            setEmailSending(false);
        }
    };

    const getStatusChip = (card: GiftCard) => {
        if (card.redeemedAt) {
            return <Chip label="Redeemed" color="default" size="small" />;
        }
        // expiresAt is a date-only value (YYYY-MM-DD): compare as strings, never via new Date()
        if (card.expiresAt && card.expiresAt <= formatDateForInput(new Date())) {
            return <Chip label="Expired" color="error" size="small" />;
        }
        return <Chip label="Active" color="success" size="small" />;
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center" gap={1}>
                    <GiftCardIcon color="primary" fontSize="large" />
                    <Box>
                        <Typography variant="h5">Gift Cards</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {total} gift card{total !== 1 ? 's' : ''} total
                        </Typography>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                >
                    Create Gift Card
                </Button>
            </Box>

            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Box mb={2}>
                <ToggleButtonGroup
                    value={statusFilter}
                    exclusive
                    onChange={(_e, val) => { if (val) setStatusFilter(val as StatusFilter); }}
                    size="small"
                >
                    <ToggleButton value="active">Active</ToggleButton>
                    <ToggleButton value="redeemed">Redeemed</ToggleButton>
                    <ToggleButton value="all">All</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
            ) : giftCards.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <GiftCardIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No gift cards have been created yet.</Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Code</TableCell>
                                <TableCell align="right">Denomination</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Expires</TableCell>
                                <TableCell>Notes</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Redeemed</TableCell>
                                <TableCell>Redeemed By</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {giftCards.map((card) => (
                                <TableRow key={card.id} hover sx={{ opacity: card.redeemedAt ? 0.6 : 1 }}>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography variant="body2" fontFamily="monospace" letterSpacing={1}>
                                                {card.code}
                                            </Typography>
                                            <Tooltip title={copiedCode === card.code ? 'Copied!' : 'Copy code'}>
                                                <span>
                                                    <Button
                                                        size="small"
                                                        sx={{ minWidth: 0, p: 0.5 }}
                                                        onClick={() => handleCopyCode(card.code)}
                                                        color={copiedCode === card.code ? 'success' : undefined}
                                                    >
                                                        <CopyIcon fontSize="small" />
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography fontWeight="bold">${parseFloat(card.denomination as unknown as string).toFixed(2)}</Typography>
                                    </TableCell>
                                    <TableCell>{getStatusChip(card)}</TableCell>
                                    <TableCell>
                                        {card.expiresAt
                                            ? parseDateOnlyString(card.expiresAt).toLocaleDateString()
                                            : <Typography variant="caption" color="text.secondary">Never</Typography>
                                        }
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {card.notes || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {new Date(card.createdAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>
                                        {card.redeemedAt
                                            ? new Date(card.redeemedAt).toLocaleDateString()
                                            : '—'
                                        }
                                    </TableCell>
                                    <TableCell>
                                        {card.redeemedByMember
                                            ? <Typography variant="body2">{card.redeemedByMember.firstName} {card.redeemedByMember.lastName}</Typography>
                                            : <Typography variant="caption" color="text.secondary">—</Typography>
                                        }
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Email gift card">
                                            <span>
                                                <Button
                                                    size="small"
                                                    sx={{ minWidth: 0, p: 0.5 }}
                                                    onClick={() => handleOpenEmailDialog(card)}
                                                    disabled={!!card.redeemedAt}
                                                >
                                                    <EmailIcon fontSize="small" />
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Email Gift Card Dialog */}
            <Dialog open={!!emailDialogCard} onClose={() => !emailSending && setEmailDialogCard(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Email Gift Card</DialogTitle>
                <DialogContent>
                    {emailDialogCard && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Sending gift card <strong>{emailDialogCard.code}</strong> (${parseFloat(String(emailDialogCard.denomination)).toFixed(2)})
                        </Typography>
                    )}
                    <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {emailFormError && <Alert severity="error">{emailFormError}</Alert>}
                        <TextField
                            label="Recipient Email"
                            type="email"
                            value={emailRecipientEmail}
                            onChange={(e) => setEmailRecipientEmail(e.target.value)}
                            fullWidth
                            required
                            autoFocus
                        />
                        <TextField
                            label="Recipient Name (optional)"
                            value={emailRecipientName}
                            onChange={(e) => setEmailRecipientName(e.target.value)}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailDialogCard(null)} disabled={emailSending}>Cancel</Button>
                    <Button
                        onClick={handleSendGiftCardEmail}
                        variant="contained"
                        disabled={!emailRecipientEmail || emailSending}
                        startIcon={emailSending ? undefined : <EmailIcon />}
                    >
                        {emailSending ? <CircularProgress size={20} /> : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create Gift Card Dialog */}
            <Dialog open={createDialogOpen} onClose={() => !creating && setCreateDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Create Gift Card</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {formError && <Alert severity="error">{formError}</Alert>}
                        <TextField
                            label="Denomination ($)"
                            type="number"
                            value={form.denomination}
                            onChange={(e) => setForm(f => ({ ...f, denomination: e.target.value }))}
                            inputProps={{ min: 0.01, step: 0.01 }}
                            fullWidth
                            required
                            autoFocus
                        />
                        <TextField
                            label="Notes (optional)"
                            value={form.notes}
                            onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                            fullWidth
                            multiline
                            rows={2}
                        />
                        <TextField
                            label="Expiry Date (optional)"
                            type="date"
                            value={form.expiresAt}
                            onChange={(e) => setForm(f => ({ ...f, expiresAt: e.target.value }))}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            helperText="Leave blank for no expiry"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>Cancel</Button>
                    <Button
                        onClick={handleCreateSubmit}
                        variant="contained"
                        disabled={!form.denomination || creating}
                    >
                        {creating ? <CircularProgress size={20} /> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default GiftCardsPage;
